// Showing a user-key column as the PERSON it points at.
//
// A transaction stores WHO as a key: a row-security owner column holds the
// acting user's 36-character platform id, a `logged_in_user()` default and the
// `:audit_*_user` columns hold its 10-character short form. That is the right
// thing to store — a name changes when somebody marries, and two "A. Kumar"s
// are the same person — and the wrong thing to show. A list headed "Asked by"
// rendered `443a07c7-526c-4417-8dae-0ee34a5b787e`, and one headed "Reviewer"
// rendered `e34a5b787e`, which identify nobody.
//
// The ENGINE answers both halves of the question, because it is the thing that
// wrote those keys: `people_columns` says which columns hold one, and `people`
// names the keys the returned rows actually carry (see the engine's `people`
// module). Deriving the rule again here is exactly what that avoids.
//
// It arrives as a context rather than a prop because every view of a record
// needs it — the grid, the compact card, the feed card, the read-only form —
// and threading one map through all of them would be noise at every level.

import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'

export interface PeopleDirectory {
  /** Form keys of the columns that hold a user key rather than a value. */
  columns: string[]
  /** user key → the person's name (or email). Only keys present in the rows. */
  names: Record<string, string>
}

const PeopleContext = createContext<PeopleDirectory | null>(null)

export function PeopleProvider(
  { value, children }: { value: PeopleDirectory | null; children: ReactNode },
) {
  return (
    <PeopleContext.Provider value={value}>{children}</PeopleContext.Provider>
  )
}

/** The people behind this screen's records, or null before the first load. */
export function usePeople(): PeopleDirectory | null {
  return useContext(PeopleContext)
}

/** Whether `formKey` is a column that holds a user key. */
export function isPersonColumn(people: PeopleDirectory | null | undefined,
                               formKey: string): boolean {
  return !!people && people.columns.includes(formKey)
}

/**
 * The person a user-key cell should read as.
 *
 * Returns null when this is not a person column, when the cell is empty, or
 * when the directory cannot place the key — callers then render the value
 * exactly as they did before. An unplaceable key is shown as it is rather than
 * blanked: a reviewer who has left the company is a fact about the record, and
 * an empty cell would say nobody reviewed it.
 */
export function personLabel(people: PeopleDirectory | null | undefined,
                            formKey: string,
                            value: unknown): string | null {
  if (!people || !isPersonColumn(people, formKey)) return null
  const key = value == null ? '' : String(value).trim()
  if (!key) return null
  return people.names[key] || null
}

/**
 * Merge the `people`/`people_columns` a response carried into what the screen
 * already knows.
 *
 * Both halves accumulate. Names because a list page names the people on it and
 * the next page names the people on that one; COLUMNS because one screen shows
 * several forms — a header and its child grids — and each answers for its own,
 * so taking only the newest would drop the header's the moment a child loaded.
 * Re-listing one object unions with itself, so the simpler case costs nothing.
 */
export function mergePeople(
  prev: PeopleDirectory | null,
  res: {
    people?: Record<string, string>
    people_columns?: string[]
    // Typed loosely on purpose: every caller passes its whole response, and
    // the rows/header are read defensively below rather than by their shape.
    rows?: unknown
    header?: unknown
  },
): PeopleDirectory | null {
  const names = { ...(prev?.names || {}), ...(res.people || {}) }
  const columns = Array.from(new Set([
    ...(prev?.columns || []), ...(res.people_columns || []),
    ...columnsHoldingKnownKeys(names, [res.rows, res.header]),
  ]))
  if (columns.length === 0) return null
  return { columns, names }
}

/**
 * The columns of these rows that hold a key the directory ALREADY names.
 *
 * The engine answers two questions, and it can answer one without the other.
 * `people_columns` is which columns it STAMPED — a row-security owner, a
 * `logged_in_user()` default, an audit user — and `people` is who the keys in
 * these rows belong to. A column that merely HOLDS somebody's id, put there by
 * a business rule or by whoever assigned the work, is not one it stamped, so it
 * is not in that list.
 *
 * A real SOP Feedback record comes back with
 * `people_columns: ["reported_by", "add_user", "chg_user"]` and
 * `people: {"443a07c7-…": "kumar.pawar"}` — while `assigned_owner` holds that
 * very key, because `reported_by` holds it too. So `personLabel` refused a name
 * the response was already carrying, and the field one row below
 * "Reported By: kumar.pawar" rendered `443a07c7-526c-4417-8dae-0ee34a5b787e`.
 *
 * A key belongs to the same person whichever column holds it. So a column whose
 * value the directory can already name is a column showing that person: no
 * lookup, no guess, nothing the response did not say itself. A column holding
 * anything else is untouched — nothing but a user key equals a user key.
 */
function columnsHoldingKnownKeys(
  names: Record<string, string>,
  sources: unknown[],
): string[] {
  if (Object.keys(names).length === 0) return []
  const out = new Set<string>()
  const rows: unknown[] = []
  for (const source of sources) {
    if (Array.isArray(source)) rows.push(...source)
    else if (source) rows.push(source)
  }
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    for (const [key, value] of Object.entries(row as Record<string, unknown>)) {
      if (typeof value !== 'string' || !key) continue
      const stored = value.trim()
      if (stored && names[stored] !== undefined) out.add(key)
    }
  }
  return Array.from(out)
}
